import { DashboardEventAttendees } from "@cmp-src/components/meeting/DashboardMeetingAttendees/DashboardEventAttendees";
import { Badge } from "@cmp-src/primitives/badge";
import {
  IResource,
  ISchedule,
  Tag,
  WorkplaceRequestElement
} from "@gettactic/api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@gettactic/components/src/primitives/tabs";
import { getScheduleAttendeeSlots } from "@gettactic/helpers/src/schedules/getScheduleAttendees";
import sanitizeHtml from "sanitize-html";
import { WPRequestsList } from "./WPRequestsList";

export const RoomData = ({
  schedule,
  resource,
  workplaceRequests
}: {
  schedule: ISchedule;
  resource: IResource | undefined;
  workplaceRequests: WorkplaceRequestElement[];
}) => {
  // One slot per attendee: a reservation holding several rooms repeats every attendee
  // once per room.
  const attendeeSlots = getScheduleAttendeeSlots(schedule);

  return (
    <Tabs
      className="space-x-2 md:space-x-4"
      defaultValue={
        workplaceRequests.length > 0 ? "WorkplaceRequests" : "attendees"
      }
    >
      <TabsList className="px-2.5 md:px-0">
        <TabsTrigger
          value="attendees"
          className="data-[state=active]:font-bold"
        >
          Attendees ({attendeeSlots.length})
        </TabsTrigger>
        <TabsTrigger value="amenities">Amenities</TabsTrigger>
        {workplaceRequests.length > 0 && (
          <TabsTrigger value="WorkplaceRequests">
            Workplace Requests ({workplaceRequests.length})
          </TabsTrigger>
        )}
      </TabsList>
      <TabsContent
        value="attendees"
        className="ml-0 space-x-0 px-2.5 md:ml-0 md:space-x-0 md:px-0"
      >
        <DashboardEventAttendees slots={attendeeSlots} />
      </TabsContent>
      <TabsContent value="amenities">
        <Amenities
          descriptions={resource?.description}
          amenities={resource?.tags}
        />
      </TabsContent>
      {workplaceRequests?.length > 0 && (
        <TabsContent
          value="WorkplaceRequests"
          className="ml-0 space-x-0 px-2.5 md:ml-0 md:space-x-0 md:px-0"
        >
          <WPRequestsList
            schedule={schedule}
            resource={resource}
            workplaceRequests={workplaceRequests}
          />
        </TabsContent>
      )}
    </Tabs>
  );
};

const Amenities = ({
  amenities,
  descriptions
}: {
  amenities: Tag[] | undefined;
  descriptions: string | undefined | null;
}) => {
  if (!amenities || amenities.length === 0) {
    if (!descriptions) {
      return <div>No amenities listed yet</div>;
    }
    return (
      <div className="prose prose-sm max-w-none">
        <div
          // biome-ignore lint/security/noDangerouslySetInnerHtml: <this is intentional as description may be markdown>
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(descriptions, {
              allowedClasses: { span: ["bg-yellow-400"] }
            })
          }}
        />
      </div>
    );
  }
  const amenitiesByCategory = amenities.reduce(
    (acc, amenity) => {
      const category = amenity.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(amenity);
      return acc;
    },
    {} as Record<string, Tag[]>
  );

  return (
    <div className="flex flex-col gap-2 pt-6">
      {Object.entries(amenitiesByCategory).map(([type, amenities]) => (
        <div key={type} className="flex flex-row flex-wrap items-center gap-2">
          <div className="font-bold">{type}: </div>
          {amenities.map((amenity) => (
            <div key={amenity.id}>
              <Badge variant={"outline"} key={amenity.id} className="text-md">
                {amenity.name}
              </Badge>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
